import React, {useEffect, useState} from 'react';
import {Platform, View} from 'react-native';
import {useStyles} from '../../../shared/hooks/styles';
import {DeviceSize} from '../../../shared/theme/helpers';
import {LoadableContent} from '../../../shared/components/loadableContent';
import {CustomScrollView} from '../../../shared/components/scrollView';
import {useScrollViewMap} from '../../../shared/components/yaMap/mobile/useScrollViewMap';
import {Button} from '../../../shared/components/button';
import {Text} from '../../../shared/components/text';
import {BaseFieldWrapper} from '../../../shared/components/baseField';
import {AddressSelect} from '../../../shared/components/address-select';
import {useIsDesktop} from '../../../shared/hooks/useIsDesktop';
import {useSelector} from 'react-redux';
import {WrappedFieldMetaProps} from 'redux-form';
import {useAppDispatch} from '../../../redux';
import {esiaSendActivateCard, esiaSendActivateCardAfterLogged, logout} from '../../../redux/actions/login';
import {refreshCardActivationDeliveryInfo} from '../../../redux/actions/cardActivation';
import {ServerValidationError} from '../../../shared/models/serverValidationError';
import {addNotification} from '../../../redux/actions/snackbar';
import {SnackbarNotificationType} from '../../../shared/components/snackbar/model';
import {esiaRegistrationInfoSelector} from '../../../redux/selectors/login';
import {DeliveryType} from '../../../types/registerCardApplicationRequestPayload';
import {CardApplicationStatus} from '../../../types/enums/cardApplicationStatus';
import {StorageKeys, storage} from '../../../shared/storage';
import {loginStepSlice} from '../../../redux/reducers/runtime/loginStep';
import {LoginState} from '../../../shared/models/login/loginStep';
import {loginSlice} from '../../../redux/reducers/login';
import {profileSlice} from '../../../redux/reducers/profile';
import {QrCodeIcon} from '../components/qrCodeIcon';
import {IssuePointSelectionMap} from './components/issuePointSelectionMap/issuePointSelectionMap';
import {hasTrackingNumber, useDeliveryMethodChange} from './useDeliveryMethodChange';
import {useAccountLinker} from '../../../shared/hooks/useAccountLinkerHook/useAccountLinker';
import {holderProfileSelector, profileTokenSelector} from '../../../redux/selectors/profile';
import {useIsSmallScreenDevice} from '../../../shared/hooks/useIsSmallScreenDevice';
import {RussianPostDeliveryInfo} from './components/russianPostDeliveryInfo';
import {IssuePointDeliveryInfo} from './components/issuePointDeliveryInfo';
import {useMap} from '../esiaRegCard/components/map/useMap';
import {getHolderProfile} from '../../../redux/actions/profile';

export default ({
  onCloseModal,
  linkedAccountIdWeb,
  onIssuePointSelectionVisibleChange,
}: {
  onCloseModal?(): void;
  linkedAccountIdWeb?: number;
  onIssuePointSelectionVisibleChange?(visible: boolean): void;
}) => {
  const isDesktop = useIsDesktop();
  const isSmallScreen = useIsSmallScreenDevice();
  const {scrollViewProps, mapProps} = useScrollViewMap();

  const styles = useStyles(({mediaOnly, theme}) => {
    return {
      formSubmitBox: {
        justifyContent: 'flex-start',
        alignItems: 'center',
        marginTop: 0,
        width: '100%',
        paddingHorizontal: 25,
        ...mediaOnly(DeviceSize.desktop, {paddingBottom: 20}),
      },
      form: {
        marginTop: 20,
        ...mediaOnly(DeviceSize.mobile, {marginTop: 0, marginBottom: 0, alignItems: 'center'}),
      },
      formSubmit: {
        paddingHorizontal: isSmallScreen ? 0 : 8,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      },
      buttonsContainer: {
        width: '100%',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      },
      mapActions: {
        position: 'relative',
        zIndex: Platform.OS === 'android' ? 2 : undefined,
        elevation: Platform.OS === 'android' ? 2 : undefined,
        backgroundColor: Platform.OS === 'android' ? theme.color.backgroundMenuColor : undefined,
      },
      button: {
        display: 'flex',
        flexDirection: 'column',
        alignContent: 'center',
        justifyContent: 'center',
        paddingHorizontal: 20,
        marginTop: 14,
        width: isDesktop ? 360 : '100%',
        maxWidth: 360,
        minWidth: 260,
        zIndex: 3,
      },
      deliveryChangeForm: {
        width: '100%',
        maxWidth: '100%',
        alignSelf: 'stretch',
      },
      addressFieldContainer: {
        ...(isDesktop
          ? {
              width: '80%',
              minWidth: 360,
            }
          : {
              width: '100%',
              maxWidth: 360,
              minWidth: 260,
            }),
        marginBottom: 10,
        marginTop: 20,
        alignSelf: 'center',
        zIndex: 3,
      },
      addressField: {
        width: '100%',
        ...(isDesktop ? {minWidth: 360} : null),
      },
      cardNumberField: {
        marginTop: 20,
        zIndex: 3,
        width: isDesktop ? 360 : '100%',
        maxWidth: 360,
        marginBottom: isDesktop ? 10 : 0,
      },
      subtitle: {
        textAlign: isDesktop ? 'center' : 'justify',
        marginTop: 10,
        marginBottom: 10,
        position: 'relative',
        ...(isDesktop ? {width: '100%', alignSelf: 'stretch' as const} : null),
      },
    };
  });

  const dispatch = useAppDispatch();
  const esiaRegInfoFromSelector = useSelector(esiaRegistrationInfoSelector);
  const {linkedAccounts, loadLinkedAccountsInfo} = useAccountLinker();
  const profile = useSelector(holderProfileSelector());
  const cabinetToken = useSelector(profileTokenSelector());
  const isLogged = !!cabinetToken;
  const isOwnAccount = linkedAccountIdWeb == null;

  useEffect(() => {
    if (!isOwnAccount) {
      return;
    }

    dispatch(getHolderProfile());
  }, [dispatch, isOwnAccount]);

  const linkedAccountRegInfo =
    linkedAccountIdWeb != null ? linkedAccounts.find((acc) => acc.id === linkedAccountIdWeb) : undefined;
  const esiaRegInfo =
    [linkedAccountRegInfo, esiaRegInfoFromSelector].find((info) => info?.cardDeliveryInfo?.deliveryTypeId != null) ??
    linkedAccountRegInfo ??
    esiaRegInfoFromSelector;

  React.useEffect(() => {
    void dispatch(refreshCardActivationDeliveryInfo());
  }, [dispatch]);

  const [loading, setLoading] = useState(false);
  const [cardNumberToActivate, setCardNumberToActivate] = useState<string>('');

  const onActivateCardClick = async (event?: any) => {
    event?.preventDefault();
    const normalizedCardNumber = (cardNumberToActivate ?? '').trim();
    if (!normalizedCardNumber) {
      dispatch(
        addNotification({
          message: 'Введите номер карты для активации',
          type: SnackbarNotificationType.Error,
        }),
      );
      return;
    }
    if (isLogged) {
      try {
        setLoading(true);
        await dispatch(
          esiaSendActivateCardAfterLogged({
            CardNumber: normalizedCardNumber,
            CounterpartyLinkId: linkedAccountIdWeb,
          }),
        );
        await loadLinkedAccountsInfo();
        if (isOwnAccount) {
          await dispatch(getHolderProfile());
        }
        if (onCloseModal) onCloseModal();
      } catch (e) {
        const error = e as ServerValidationError;
        let errorText = error.data?.[0].message || error.message || 'Ошибка. Повторите еще раз';
        dispatch(
          addNotification({
            message: errorText,
            type: SnackbarNotificationType.Error,
          }),
        );
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      setLoading(true);
      const token = await storage.getItem(StorageKeys.activationToken);
      await dispatch(esiaSendActivateCard({CardNumber: normalizedCardNumber}));
      await storage.setItem(StorageKeys.token, token);
      await storage.removeItem(StorageKeys.activationToken);
      await storage.removeItem(StorageKeys.esiaRegInfo);
      if (Platform.OS === 'web') window.location.reload();
      else {
        await dispatch(loginSlice.actions.setEsiaUserHasNoCards(false));
        await dispatch(profileSlice.actions.reset(null));
        await dispatch(profileSlice.actions.setToken(token));
      }
    } catch (e) {
      const error = e as ServerValidationError;
      let errorText = error.data?.[0].message || error.message || 'Ошибка. Повторите еще раз';
      dispatch(
        addNotification({
          message: errorText,
          type: SnackbarNotificationType.Error,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  function onOrderFormClose(): void {
    dispatch(logout());
    dispatch(loginStepSlice.actions.setLoginStep(LoginState.enter));
    onCloseModal?.();
  }

  const trackingNumberIsFilled = React.useMemo(
    () => hasTrackingNumber(isOwnAccount ? profile : null, esiaRegInfo),
    [isOwnAccount, profile, esiaRegInfo],
  );

  const activationIsUnavailable = React.useMemo(() => {
    const hasApprovedPrerequestedApplication = isOwnAccount
      ? profile?.prerequestedCardApplicationId != null
      : esiaRegInfo?.cardApplicationStatusId === CardApplicationStatus.Accepted;
    const hasCreatedPrerequestedApplication =
      esiaRegInfo?.cardApplicationStatusId === CardApplicationStatus.AcceptFromClient;

    const deliveryTypeId = isOwnAccount
      ? profile?.prerequestedCardApplicationDeliveryTypeId ?? esiaRegInfo?.cardDeliveryInfo?.deliveryTypeId
      : esiaRegInfo?.cardDeliveryInfo?.deliveryTypeId;

    return (
      (hasApprovedPrerequestedApplication || hasCreatedPrerequestedApplication) &&
      deliveryTypeId === DeliveryType.RussianPost &&
      !trackingNumberIsFilled
    );
  }, [isOwnAccount, trackingNumberIsFilled, profile, esiaRegInfo]);

  const issuePointsMap = useMap({showOnlyAvailableCards: !activationIsUnavailable});

  const deliveryMethodChange = useDeliveryMethodChange({
    activationIsUnavailable,
    isOwnAccount: linkedAccountIdWeb == null,
    cardApplicationId: profile?.prerequestedCardApplicationId,
    profile,
    esiaRegInfo,
    issuePointsMap,
  });

  useEffect(() => {
    onIssuePointSelectionVisibleChange?.(deliveryMethodChange.isIssuePointSelectionVisible);
  }, [deliveryMethodChange.isIssuePointSelectionVisible, onIssuePointSelectionVisibleChange]);

  return (
    <CustomScrollView
      contentContainerStyle={styles.formSubmitBox}
      style={{width: '100%'}}
      {...scrollViewProps}
      withRefresh={false}
      contentInsetAdjustmentBehavior='automatic'>
      <LoadableContent loading={loading} style={{width: '100%'}}>
        {!loading ? (
          <View style={styles.form}>
            {!deliveryMethodChange.isIssuePointSelectionVisible &&
              !deliveryMethodChange.isRussianPostAddressInputVisible && (
                <>
                  {esiaRegInfo?.cardDeliveryInfo?.deliveryTypeId === DeliveryType.RussianPost ? (
                    <RussianPostDeliveryInfo
                      esiaRegInfo={esiaRegInfo}
                      contentAlign={isDesktop ? 'center' : 'justify'}
                      canChangeDeliveryMethod={deliveryMethodChange.canChangeDeliveryMethod}
                    />
                  ) : (
                    <IssuePointDeliveryInfo
                      esiaRegInfo={esiaRegInfo}
                      linkedAccountIdWeb={linkedAccountIdWeb}
                      viewVariantForWeb='esiaActivateCard'
                      issuePointsMap={issuePointsMap}
                      contentAlign={isDesktop ? 'center' : 'justify'}
                      canChangeDeliveryMethod={deliveryMethodChange.canChangeDeliveryMethod}
                    />
                  )}

                  <View testID='styles.formSubmitBtnContainer' style={styles.formSubmit}>
                    {!activationIsUnavailable && (
                      <>
                        <BaseFieldWrapper
                          onCustomBlur={() => {}}
                          input={{
                            value: cardNumberToActivate,
                            onChange: (e) => setCardNumberToActivate(e ?? ''),
                          }}
                          label={
                            Platform.OS !== 'web' ? 'Номер карты для активации' : 'Укажите номер карты для активации'
                          }
                          type='digits'
                          autoCapitalize='none'
                          name='cardNumToActivate'
                          readOnly={false}
                          inputContainerStyle={
                            Platform.OS !== 'web' ? {width: Platform.OS === 'ios' ? '90%' : '100%'} : undefined
                          }
                          style={styles.cardNumberField}
                          right={
                            Platform.OS !== 'web' ? (
                              <QrCodeIcon onScan={setCardNumberToActivate} adjustFor='input' />
                            ) : undefined
                          }
                        />

                        <Button
                          style={styles.button}
                          disabled={loading}
                          onPress={onActivateCardClick}
                          text='Активировать карту'
                          id='esia-active-card-form-submit'
                        />
                      </>
                    )}

                    {deliveryMethodChange.canChangeDeliveryMethod && (
                      <Button
                        style={[styles.button, {height: 'auto', minHeight: 40}]}
                        disabled={loading}
                        onPress={deliveryMethodChange.openDeliveryMethodChange}
                        text='Изменить способ получения'
                        id='esia-select-issuepoint-btn'
                      />
                    )}

                    {Platform.OS === 'web' &&
                      isDesktop &&
                      !isLogged &&
                      !deliveryMethodChange.isIssuePointSelectionVisible && (
                        <Button
                          style={styles.button}
                          disabled={loading}
                          onPress={onOrderFormClose}
                          text='Выход'
                          type='secondary'
                          id='esia-form-close'
                        />
                      )}
                  </View>
                </>
              )}

            {deliveryMethodChange.isRussianPostAddressInputVisible && (
              <View style={styles.deliveryChangeForm}>
                <Text type='h6Light' style={styles.subtitle}>
                  Вы меняете способ получения карты на Почту России. Укажите адрес, на который нужно доставить карту.
                </Text>
                <View style={styles.addressFieldContainer}>
                  <AddressSelect
                    value={deliveryMethodChange.russianPostDeliveryAddress}
                    onChange={deliveryMethodChange.setRussianPostDeliveryAddress}
                    setAddress={deliveryMethodChange.setRussianPostDeliveryAddress}
                    label='Адрес получателя'
                    name='russianPostDeliveryAddress'
                    baseFieldProps={{
                      style: styles.addressField,
                      autoFocus: true,
                      meta: {
                        error: {error: 'Адрес должен относиться к Свердловской области'},
                        touched: !deliveryMethodChange.isRussianPostDeliveryAddressValid,
                      } as WrappedFieldMetaProps,
                    }}
                  />
                </View>

                <View testID='styles.buttonsContainer' style={styles.buttonsContainer}>
                  <Button
                    style={styles.button}
                    disabled={
                      loading ||
                      deliveryMethodChange.deliveryChangeSubmitting ||
                      !deliveryMethodChange.russianPostDeliveryAddressText ||
                      !deliveryMethodChange.isRussianPostDeliveryAddressValid
                    }
                    onPress={deliveryMethodChange.onConfirmRussianPost}
                    text='Сохранить изменения'
                    id='russianpost-delivery-change-submit'
                  />

                  <Button
                    style={styles.button}
                    onPress={deliveryMethodChange.closeDeliveryMethodChange}
                    text='Отмена'
                    type='secondary'
                    id='russianpost-delivery-change-back'
                  />
                </View>
              </View>
            )}

            {deliveryMethodChange.isIssuePointSelectionVisible && (
              <View style={styles.deliveryChangeForm}>
                <IssuePointSelectionMap
                  mapProps={mapProps}
                  mapCenterPoint={deliveryMethodChange.mapCenterPoint}
                  currentMarker={deliveryMethodChange.currentMarker}
                  mapLoading={deliveryMethodChange.mapLoading}
                  mapData={deliveryMethodChange.mapData}
                  nativeRegion={deliveryMethodChange.nativeRegion}
                  onIssuePointSelected={deliveryMethodChange.onIssuePointSelected}
                  onNativeIssuePointSelected={deliveryMethodChange.onNativeIssuePointSelected}
                  lastMarkerClickTimestamp={deliveryMethodChange.lastMarkerClickTimestamp}
                  setLastMarkerClickTimestamp={deliveryMethodChange.setLastMarkerClickTimestamp}
                  destroyNativeMap={deliveryMethodChange.destroyNativeMap}
                  markers={deliveryMethodChange.markers}
                  embeddedInModal={!!onCloseModal}
                  onPressMarker={deliveryMethodChange.onPressMarker}
                  mapZoomLevel={deliveryMethodChange.mapZoomLevel}
                  isIssuePointDetailsVisible={deliveryMethodChange.isIssuePointDetailsVisible}
                  setIsIssuePointDetailsVisible={deliveryMethodChange.setIsIssuePointDetailsVisible}
                />

                <View testID='styles.buttonsContainer' style={[styles.buttonsContainer, styles.mapActions]}>
                  <Button
                    style={styles.button}
                    disabled={
                      loading || deliveryMethodChange.deliveryChangeSubmitting || !deliveryMethodChange.currentMarker
                    }
                    onPress={deliveryMethodChange.onConfirmIssuePoint}
                    text='Сохранить изменения'
                    id='issuepoint-delivery-change-submit'
                  />

                  <Button
                    style={styles.button}
                    onPress={deliveryMethodChange.closeDeliveryMethodChange}
                    text='Отмена'
                    type='secondary'
                    id='issuepoint-delivery-change-back'
                  />
                </View>
              </View>
            )}
          </View>
        ) : null}
      </LoadableContent>
    </CustomScrollView>
  );
};
